import { useMemo, type ComponentType } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button, Caption1, MessageBar, MessageBarBody, Spinner, makeStyles, mergeClasses } from '@fluentui/react-components';
import { ShellIcons, shellMedia, tokens } from '@vigeo/ui';
import { AGENCY_TIME_ZONE, isComplianceTracked, type Person } from '@vigeo/shared';
import { DASHBOARD_ASIDES, DASHBOARD_CARDS, DASHBOARD_CARD_HIDDEN, DEFAULT_DASHBOARD_LAYOUT, type DashboardCardProps } from '@/features/dashboard';
import { roleLabelForKeys, usePeopleReadiness } from '@/features/candidates';
import { fetchCatalogOptions, fetchDashboard, fetchHealth, fetchOffers, fetchPeople } from '@/lib/api';
import { fetchAuthState } from '@/lib/auth';

/**
 * The staff dashboard — frame 06b `288:15` ("0.1 · HR · Dashboard — home, V4", #265).
 *
 * THE FRAME'S STRUCTURE, NOT A GRID OF EQUAL CARDS. It draws three rows: a wide "Waiting on HR"
 * beside a narrow "Coming up this week", a full-width "Hiring", then "Roster" and "Credential
 * expiry" side by side. The rows live in `DEFAULT_DASHBOARD_LAYOUT`; this page renders whichever
 * ones it names. That indirection is the point (06b 0.1): what this region shows is expected to
 * change, so changing it should not mean editing this file.
 *
 * FROM AUTHORITATIVE QUERIES ONLY (Journey G). Every figure is the count of the records it names at
 * the moment of the request; the page stores nothing and estimates nothing. A failed request shows
 * the failure — it never substitutes a sample.
 *
 * FOUR REQUESTS, ALL OF THEM SHARED. `/api/dashboard` and `/api/offers` are the screens' own; the
 * people list and the per-person checklists are the exact queries the Roster and every Person page
 * already run, under the same keys, so this page warms their cache rather than duplicating it.
 */

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXL },
  head: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: tokens.spacingHorizontalL, flexWrap: 'wrap' },
  titles: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, minWidth: 0 },
  title: { margin: 0, fontSize: tokens.fontSizeHero700, lineHeight: tokens.lineHeightHero700, fontWeight: tokens.fontWeightSemibold },
  /**
   * One row of the frame's composition. Cards grow to share the width; an ASIDE holds the frame's
   * fixed 376px (23.5rem) instead. Below the desktop tier everything stacks, including the aside —
   * a 23.5rem column beside anything is narrower than the phone it is on.
   */
  row: { display: 'flex', flexDirection: 'row', alignItems: 'stretch', gap: tokens.spacingHorizontalXL, [shellMedia.belowTablet]: { flexDirection: 'column' } },
  // `display: flex` plus a growing child, so a card is as tall as the tallest one beside it — the
  // frame's cards are equal height in every row, and `alignItems: stretch` alone only stretches
  // this wrapper, not the card inside it.
  grow: { display: 'flex', flexGrow: 1, flexBasis: 0, minWidth: 0, '& > *': { flexGrow: 1 } },
  aside: { display: 'flex', flexGrow: 0, flexShrink: 0, flexBasis: '23.5rem', minWidth: 0, '& > *': { flexGrow: 1 }, [shellMedia.belowTablet]: { flexBasis: 'auto' } },
  centered: { display: 'flex', justifyContent: 'center', paddingBlock: tokens.spacingVerticalXXXL },
  empty: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    padding: tokens.spacingHorizontalL,
  },
});

/**
 * Today where the agency is, not where the browser is. An HR admin in another timezone must not see
 * a different day on the same screen, and near midnight the browser's date is simply the wrong one.
 */
function agencyToday(now: Date): string {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: AGENCY_TIME_ZONE,
  }).format(now);
}

const NO_PEOPLE: readonly Person[] = [];

export function DashboardPage({ layout = DEFAULT_DASHBOARD_LAYOUT }: { readonly layout?: readonly (readonly string[])[] } = {}) {
  const styles = useStyles();
  const dashboard = useQuery({ queryKey: ['dashboard'], queryFn: fetchDashboard });
  const offers = useQuery({ queryKey: ['offers'], queryFn: fetchOffers });
  const people = useQuery({ queryKey: ['people'], queryFn: () => fetchPeople() });
  const options = useQuery({ queryKey: ['catalog-options'], queryFn: fetchCatalogOptions, staleTime: Infinity });
  const health = useQuery({ queryKey: ['health'], queryFn: fetchHealth });
  const auth = useQuery({ queryKey: ['auth'], queryFn: fetchAuthState, retry: false });
  const canEdit = auth.data?.status === 'signed-in' && auth.data.user.isAdmin;
  const navigate = useNavigate();

  const all = people.data ?? NO_PEOPLE;
  /**
   * The people whose readiness this page derives: those being onboarded (the Hiring card's last
   * three stages) and those the agency employs (the Roster card's bands). Candidates are NOT among
   * them — their stage comes from their offer, so a checklist per candidate would be a request per
   * person for an answer this page does not read.
   */
  const needChecklists = useMemo(
    // Staff added by hand are not tracked yet (ADR-016), so no checklist is derived for them either.
    () => all.filter((person) => person.lifecycle === 'onboarding' || ((person.lifecycle === 'active' || person.lifecycle === 'inactive') && isComplianceTracked(person))),
    [all],
  );
  const readiness = usePeopleReadiness(needChecklists);
  const roleOf = useMemo(() => (keys: readonly string[]) => roleLabelForKeys(keys, options.data), [options.data]);

  // An unknown key is skipped rather than thrown: once the layout can come from settings, one bad
  // entry must cost its own card, never the whole dashboard.
  // Deduped as well as filtered, ACROSS rows. A layout from settings is exactly as likely to repeat
  // a key as to misspell one, and a repeat is the worse failure: two React children with the same
  // key, and two sections sharing one `aria-labelledby` id, so both are announced by the first
  // heading.
  const seen = new Set<string>();
  const rows = dashboard.data === undefined ? [] : layout
    .map((row) => row
      .filter((key) => !seen.has(key) && (seen.add(key), true))
      .map((key) => ({ key, Card: DASHBOARD_CARDS[key] }))
      .filter((entry): entry is { key: string; Card: ComponentType<DashboardCardProps> } => entry.Card !== undefined)
      /**
       * Asked BEFORE the card renders, so a card with nothing to say costs no element and no row
       * gap — "Overdue updates" is absent on a healthy morning rather than an empty red band.
       */
      .filter(({ key }) => DASHBOARD_CARD_HIDDEN[key]?.({
        data: dashboard.data, offers: offers.data?.rows, unreadable: offers.data?.unavailable, people: all, readiness, roleOf,
      }) !== true))
    .filter((row) => row.length > 0);

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <div className={styles.titles}>
          <h1 className={styles.title}>Dashboard</h1>
          <Caption1>{agencyToday(new Date())}</Caption1>
        </div>
        {/*
          * Routed, not an anchor: an href here would reload the whole app to reach a page the
          * router already has.
          *
          * `/app/people/new` is row 0.2's page (#258, PR #296) and NOT the routed-state pattern
          * this button used to carry. That one opened the CSV importer under an "Add a candidate"
          * heading with no way to close it, and its `'add'` arm is being removed from
          * `CandidatesPage`. The route arrives with #296; see the PR body.
          */}
        {canEdit ? (
          <Button appearance="primary" icon={<ShellIcons.plus />} onClick={() => { navigate('/app/people/new'); }}>
            Add candidate
          </Button>
        ) : null}
      </div>

      {/*
        * The API's health is SHOWN ONLY WHEN IT IS BAD. The frame's header carries a title, the
        * date and one button — no status chip — but a dashboard that cannot say the API is down is
        * worse than one that is a chip short, so the signal moved from a permanent badge to a bar
        * that appears when it means something.
        */}
      {health.isError ? (
        <MessageBar intent="error">
          <MessageBarBody>The API is unreachable, so these figures may be stale.</MessageBarBody>
        </MessageBar>
      ) : null}

      {dashboard.isPending ? (
        <div className={styles.centered}>
          <Spinner label="Loading the dashboard" />
        </div>
      ) : dashboard.isError ? (
        <MessageBar intent="error">
          <MessageBarBody>The dashboard could not be loaded. {dashboard.error.message}</MessageBarBody>
        </MessageBar>
      ) : rows.length === 0 ? (
        // A layout naming nothing is a deliberate state, not a loading one, so it says so.
        <section className={styles.empty} aria-label="Nothing on the dashboard">
          <Caption1>Nothing is set to show here yet.</Caption1>
        </section>
      ) : (
        rows.map((row, index) => (
          // The index is the key because a row has no identity of its own — its cards do, and they
          // carry their own keys below.
          <div key={index} className={styles.row}>
            {row.map(({ key, Card }) => (
              // mergeClasses, never a template literal: Griffel class strings carry a sequence
              // hash, and concatenating two makes Fluent's own merge discard the second.
              <div key={key} className={mergeClasses(DASHBOARD_ASIDES.has(key) ? styles.aside : styles.grow)}>
                <Card
                  data={dashboard.data}
                  offers={offers.data?.rows}
                  unreadable={offers.data?.unavailable}
                  people={all}
                  readiness={readiness}
                  roleOf={roleOf}
                />
              </div>
            ))}
          </div>
        ))
      )}
    </div>
  );
}
